import { QrCode, Building2, MapPin, Plane, Copy, Check } from "lucide-react";
import { useState } from "react";

export const ExitPassTicket = ({ pass }) => {
  const [copied, setCopied] = useState(false);

  if (!pass) return null;

  const copyCode = () => {
    if (pass.pass_code) {
      navigator.clipboard.writeText(pass.pass_code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div className="pass-ticket">
      {/* Header */}
      <div className="pass-header">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-emerald-100 text-xs uppercase tracking-wider mb-1">Exit Pass</p>
            <h3 className="font-heading font-bold text-xl">{pass.staff_name}</h3>
            <p className="font-mono text-emerald-200 text-sm">{pass.employee_id}</p>
          </div>
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <QrCode className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
        </div>
      </div>
      
      {/* Details */}
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-start gap-2">
            <Building2 className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider">Department</p>
              <p className="text-sm font-medium text-stone-700">{pass.department_name || "N/A"}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-stone-400 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-xs text-stone-400 uppercase tracking-wider">Destination</p>
              <p className="text-sm font-medium text-stone-700">{pass.destination}</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-stone-50 rounded-xl p-3">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Departure</p>
            <p className="font-medium text-stone-700">{pass.departure_date}</p>
            <p className="text-sm text-stone-500">{pass.departure_time}</p>
          </div>
          <div className="bg-stone-50 rounded-xl p-3">
            <p className="text-xs text-stone-400 uppercase tracking-wider mb-1">Return</p>
            <p className="font-medium text-stone-700">{pass.arrival_date}</p>
            <p className="text-sm text-stone-500">{pass.arrival_time}</p>
          </div>
        </div>

        {/* 6-Digit Pass Code */}
        {pass.pass_code && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] text-emerald-600 uppercase tracking-wider font-semibold mb-0.5">Security Pass Code</p>
                <span 
                  className="font-mono text-xl font-bold tracking-[0.2em] text-emerald-800"
                  data-testid="pass-code-display"
                >
                  {pass.pass_code}
                </span>
              </div>
              <button
                onClick={copyCode}
                className="p-2 rounded-lg hover:bg-emerald-100 transition-colors"
                data-testid="copy-pass-code-btn"
                title="Copy code"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Copy className="w-4 h-4 text-emerald-500" />
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Dashed separator */}
        <div className="border-t-2 border-dashed border-stone-200 my-4" />
        
        {/* QR Code */}
        {pass.qr_code && (
          <div className="flex flex-col items-center">
            <div className="bg-white p-4 rounded-xl shadow-inner border border-stone-200">
              <img
                src={`data:image/png;base64,${pass.qr_code}`}
                alt="Exit Pass QR Code"
                className="w-40 h-40"
                data-testid="qr-code-image"
              />
            </div>
            <p className="text-xs text-stone-400 mt-3 text-center">
              Scan this QR code at security checkpoint
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExitPassTicket;
